import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { HeadTag } from 'shared/components';
import {
  Box,
  CircularProgress,
  debounce,
  Paper,
  Stack,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import EmptyPlaceholder from 'shared/components/EmptyPlaceholder';
import {
  AppFormikSelectWithKeys,
  AppFormikTextField,
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
} from 'shared/components/AppComponents';
import { ReactComponent as Search } from 'assets/images/ic_search.svg';
import { Formik } from 'formik';
import PageHeader from 'shared/components/PageHeader';
import { ReactComponent as Plus } from 'assets/images/ic_plus.svg';
import { useNavigate } from 'react-router-dom';
import { RiskRegisterService } from 'services/RiskRegisterService';
import { Utils } from 'shared/helpers';
import { IAISystem } from 'modules/ai-inventory/ai-system/components/AISystemRecorder';
import { IDataset } from 'modules/ai-inventory/datasets/records-dataset/Main';
import { useDispatch, useSelector } from 'react-redux';
import { IAppState } from 'state/RootReducer';
import { PaginationActions } from 'state/pagination/Action';
import { BaseApplicationState } from 'state/baseapplication/Reducer';
import useSize from 'shared/components/Hooks';

export interface IRiskRegister {
  _id?: string;
  riskTitle: string;
  riskDescription: string;
  affectedStakeholders: string;
  mitigationPlan: string;
  residualRiskOwner: string;
  dueDate: string;
  riskType: string;
  likelihood: string;
  impact: string;
  riskSource: string;
  mitigationStatus: string;
  residualRiskLevel: string;
  aisystemId?: IAISystem[];
  datasetId?: IDataset[];
}

// const SearchFormikContext = ({ onChange }: any): any => {
//   const { values } = useFormikContext();

//   useEffect(() => {
//     onChange(values);
//   }, [values]);

//   return null;
// };

const STATUS_FILTERS = [
  { key: '', label: 'All Statuses' },
  { key: 'Not Started', label: 'Not Started' },
  { key: 'In Progress', label: 'In Progress' },
  { key: 'Mitigated', label: 'Mitigated' },
  { key: 'Accepted', label: 'Accepted' },
  { key: 'Archived', label: 'Archived' },
];

// const DEFAULT_STATUSES = [
//   'Mitigated',
//   'Accepted',
//   'Not Started',
//   'In Progress',
//   'Archived',
// ];

const RiskRegisterList = (): ReactElement => {
  const dispatch = useDispatch();
  const risksPagination = useSelector(
    (state: IAppState) => state.paginationState.risks
  );
  const { orgACLConfig }: BaseApplicationState = useSelector(
    (state: any) => state.baseAppState
  );

  const [isLoadingRecords, setIsLoadingRecords] = useState(false);
  const [riskRegisters, setRiskRegisters] = useState<IRiskRegister[]>([]);

  const { isDesktop, isMobile } = useSize();
  const navigate = useNavigate();

  // const [selectedStatuses, setSelectedStatuses] =
  //   useState<string[]>(DEFAULT_STATUSES);
  // const [searchValue, setSearchValue] = useState('');

  // API Calls
  const loadRiskRegisters = (): void => {
    setIsLoadingRecords(true);
    const queryString = new URLSearchParams({
      offset: String(risksPagination.offset),
      limit: String(risksPagination.pageLimit),
      title: risksPagination.search,
      status: risksPagination.status,
    }).toString();
    RiskRegisterService.getAll(queryString)
      .then((response) => {
        const { risks, pagination } = response.data.data;
        setRiskRegisters(risks);

        dispatch(
          PaginationActions.setPagination({
            risks: {
              offset: pagination.offset,
              pageLimit: pagination.limit,
              totalRecords: pagination.total,
              totalPages: pagination.totalPages,
            },
          })
        );
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => {
        setIsLoadingRecords(false);
      });
  };

  // const handleFilterChange = (values: any, statusOverride?: string[]): any => {
  //   const { search } = values;
  //   const statuses =
  //     statusOverride !== undefined ? statusOverride : selectedStatuses;
  //   setSearchValue(search);
  //   if (!riskRegisters.length) return;
  //   const filtered: any = riskRegisters.filter((item) => {
  //     const matchesSearch = search
  //       ? item.riskTitle.toLowerCase().includes(search.toLowerCase())
  //       : true;

  //     const matchesStatus =
  //       statuses.length > 0 ? statuses.includes(item.mitigationStatus) : true;

  //     return matchesSearch && matchesStatus;
  //   });

  //   setFilteredRegisters(filtered);
  // };

  // const handleToggleStatus = (status: string): void => {
  //   const data = selectedStatuses.includes(status)
  //     ? selectedStatuses.filter((s) => s !== status)
  //     : [...selectedStatuses, status];

  //   setSelectedStatuses(data);
  //   handleFilterChange({ search: searchValue }, data);
  // };

  // const handleResetToDefaults = (): void => {
  //   setSelectedStatuses(DEFAULT_STATUSES);
  //   handleFilterChange({ search: searchValue }, DEFAULT_STATUSES);
  // };

  const debouncedSearch = useCallback(
    debounce((searchValue: any) => {
      dispatch(
        PaginationActions.setPagination({
          risks: {
            ...risksPagination,
            offset: 1,
            search: searchValue,
          },
        })
      );
    }, 500),
    [risksPagination]
  );

  const handlePageChange = (_event: unknown, page: number): void => {
    dispatch(
      PaginationActions.setPagination({
        risks: {
          offset: page + 1,
        },
      })
    );
  };

  const handleStatusChange = (statusValue: string): void => {
    dispatch(
      PaginationActions.setPagination({
        risks: {
          ...risksPagination,
          offset: 1,
          status: statusValue,
        },
      })
    );
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const pageLimit = Number(event.target.value);

    dispatch(
      PaginationActions.setPagination({
        risks: {
          offset: 1,
          pageLimit,
        },
      })
    );
  };

  const handleClearFilter = (): void => {
    dispatch(
      PaginationActions.setPagination({
        risks: {
          status: '',
          search: '',
        },
      })
    );
  };

  // Hooks
  useEffect(() => {
    loadRiskRegisters();
  }, [
    risksPagination.status,
    risksPagination.offset,
    risksPagination.pageLimit,
    risksPagination.search,
  ]);

  // UI Elements
  return (
    <>
      <PageHeader
        title="AI Risk Register"
        description=""
        actions={
          <>
            {' '}
            {orgACLConfig.riskManagement?.includes(
              'riskManagement.riskRegister.addRisk'
            ) && (
              <AppPrimaryButton
                className="btn-small"
                onClick={() => navigate('/risk-register/add')}
                startIcon={
                  <SvgIcon
                    component={Plus}
                    inheritViewBox
                    className="view-icon"
                    fontSize="inherit"
                  />
                }
                title="Add Risk"
              />
            )}
          </>
        }
      />
      <Box className="risk-management risk-register">
        <HeadTag title="Risk Register" />

        <Box className="risk-register-wrapper">
          <Stack direction="column" spacing={2}>
            <Stack
              direction={isMobile ? 'column' : 'row'}
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
              spacing={2}
            >
              <Typography variant="h6">Risks</Typography>
              <Box
                className="search-risk-wrapper"
                sx={{ alignItems: 'center' }}
              >
                <Formik
                  initialValues={{
                    search: risksPagination.search,
                    status: risksPagination.status,
                  }}
                  enableReinitialize
                  onSubmit={() => {
                    console.log('search');
                  }}
                >
                  {({ resetForm }) => (
                    <Stack
                      direction={isMobile ? 'column' : 'row'}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      spacing={2}
                    >
                      <AppFormikSelectWithKeys
                        name="status"
                        className="rework-selection"
                        notched={false}
                        labelId="fix-selection-label"
                        testid="solicitor-dropdown"
                        MenuProps={{
                          className: 'menu-fix-selection single-fix-rework',
                        }}
                        outerLabel
                        placeholder="Filter Status"
                        notEmphasized
                        notSelectablePlaceholder={false}
                        label=""
                        titleKey="label"
                        valueKey="key"
                        items={STATUS_FILTERS}
                        onChange={(e: any) =>
                          handleStatusChange(e.target.value)
                        }
                      />
                      <AppFormikTextField
                        outerLabel={false}
                        name="search"
                        placeholder="Search risks.."
                        inputProps={{
                          startAdornment: (
                            <SvgIcon
                              component={Search}
                              inheritViewBox
                              className="view-icon"
                              fontSize="inherit"
                              htmlColor="#76A81F"
                            />
                          ),
                        }}
                        onChange={(e: any) => {
                          debouncedSearch(e.target.value);
                        }}
                      />

                      <AppPrimaryButtonOutlined
                        className="btn-small"
                        fullWidth
                        title={isDesktop ? 'Clear Filters' : 'Clear'}
                        onClick={() => {
                          resetForm();
                          handleClearFilter();
                        }}
                      />
                    </Stack>
                  )}
                </Formik>
              </Box>
            </Stack>

            {isLoadingRecords && (
              <Box className="empty-content">
                <CircularProgress />
              </Box>
            )}
            {!isLoadingRecords && (
              <TableContainer component={Paper}>
                <Table
                  className="app-table"
                  sx={{ minWidth: 650 }}
                  aria-label="simple table"
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>Risk title</TableCell>
                      <TableCell>Risk type</TableCell>
                      <TableCell>Likelihood</TableCell>
                      <TableCell>Impact</TableCell>
                      <TableCell>Mitigation Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {riskRegisters.length > 0 ? (
                      riskRegisters.map((row: any) => (
                        <TableRow
                          key={row._id}
                          sx={{
                            '& .MuiTableCell-root': { whiteSpace: 'nowrap' },
                            '&:last-child td, &:last-child th': {
                              border: 0,
                            },
                          }}
                        >
                          <TableCell component="th" scope="row">
                            {row.riskTitle}
                          </TableCell>
                          <TableCell>{row.riskType}</TableCell>
                          <TableCell>{row.likelihood}</TableCell>
                          <TableCell>{row.impact}</TableCell>
                          <TableCell>{row.mitigationStatus}</TableCell>
                          <TableCell>
                            {orgACLConfig.riskManagement?.includes(
                              'riskManagement.riskRegister.view'
                            ) && (
                              <AppPrimaryButtonOutlined
                                className="btn-extra-small primary-light-fill"
                                onClick={() => {
                                  navigate(`detail/${row._id}`);
                                }}
                                title="View"
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <EmptyPlaceholder title="No risks found." />
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        </Box>
        {!isLoadingRecords && riskRegisters.length > 0 && (
          <TablePagination
            component="div"
            count={risksPagination.totalRecords}
            page={risksPagination.offset - 1}
            rowsPerPage={risksPagination.pageLimit}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[10, 20, 50]}
            disabled={isLoadingRecords}
          />
        )}
      </Box>
    </>
  );
};

export default RiskRegisterList;
